/**
 * "Add element to cycle" (f11.2) -- band 2 of the legacy Input Sheet, as a
 * choice from the confirmed US Engagements master rather than a typed row.
 *
 * The prototype's `modal-scrim` / `modal` shape through the shared `Modal`:
 * a debounced search, a radio list of candidates the SERVER chose (master,
 * scope, grant, not yet in the cycle), and an optional delivery country.
 * Server refusals stay inline; a `409 ELEMENT_ALREADY_IN_CYCLE` refetches the
 * list, so the element that was just taken disappears from it.
 */

import { useMemo, useState } from 'react';
import { Plus, Search } from 'lucide-react';

import { ApiError } from '@/api/client';
import type { ElementCandidate } from '@/api/client';
import { useCountries } from '@/api/queries';
import { DialogField, Modal } from '@/routes/reference-data/Modal';

import { useAddElement } from './useAddElement';
import { useElementCandidates } from './useElementCandidates';

export const EMPTY_TEXT =
  'No elements available. Every element of this unit in the confirmed US Engagements master is already in the cycle.';
export const NO_MASTER_TEXT = 'The US Engagements master (step 1) has not been committed for this cycle.';

function CandidateRow({
  candidate,
  chosen,
  onChoose,
}: {
  candidate: ElementCandidate;
  chosen: boolean;
  onChoose: () => void;
}) {
  return (
    <li>
      <label className={`owner-choice ae-choice${chosen ? ' is-chosen' : ''}`}>
        <input
          type="radio"
          name="engagement_element_id"
          value={candidate.engagement_element_id}
          checked={chosen}
          onChange={onChoose}
        />
        <span className="user-meta">
          <span className="ae-line">
            <span className="mono ae-code">{candidate.code}</span>
            {candidate.name && <span className="ae-name">{candidate.name}</span>}
          </span>
          <span className="owner-mail">
            {candidate.engagement.name} · {candidate.client.name}
            {candidate.product ? ` · ${candidate.product}` : ''}
          </span>
        </span>
        {candidate.stage_text && <span className="badge badge-muted owner-status">{candidate.stage_text}</span>}
      </label>
    </li>
  );
}

export function AddElementModal({
  cycleId,
  cycleCode,
  businessUnitId,
  businessUnitName,
  onClose,
  onAdded,
}: {
  cycleId: string;
  cycleCode: string;
  businessUnitId: string;
  businessUnitName: string;
  onClose: () => void;
  onAdded: (elementCode: string) => void;
}) {
  const [search, setSearch] = useState('');
  const [chosenId, setChosenId] = useState('');
  const [countryId, setCountryId] = useState('');
  const candidates = useElementCandidates(cycleId, businessUnitId, search);
  const countries = useCountries();
  const add = useAddElement(cycleId, businessUnitId);

  const rows = candidates.data?.candidates ?? [];
  const chosen = rows.find((row) => row.engagement_element_id === chosenId);
  const activeCountries = useMemo(
    () =>
      (countries.data?.countries ?? [])
        .filter((country) => country.is_active)
        .sort((left, right) => left.name.localeCompare(right.name)),
    [countries.data],
  );

  function submit() {
    if (!chosen || add.isPending) return;
    add.mutate(
      { engagement_element_id: chosen.engagement_element_id, country_id: countryId || null },
      { onSuccess: (result) => onAdded(result.line.element_code) },
    );
  }

  const failure = add.error instanceof ApiError ? add.error.message : add.error ? 'The element could not be added.' : null;
  const masterMissing = candidates.data?.master_committed === false;

  return (
    <Modal
      title={`Add element to cycle ${cycleCode}`}
      subtitle={businessUnitName}
      onClose={onClose}
      onSubmit={submit}
      busy={add.isPending}
      submitDisabled={!chosen}
      submitLabel="Add element"
      submitIcon={<Plus aria-hidden="true" />}
      layout="plain"
      message={failure}
    >
      <div className="search-field owner-search">
        <Search aria-hidden="true" />
        <input
          type="search"
          placeholder="Search element code, name or client"
          aria-label="Search element code, name or client"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {candidates.isLoading ? (
        <div className="hint">Loading the master list…</div>
      ) : masterMissing ? (
        <div className="empty ae-empty"><p>{NO_MASTER_TEXT}</p></div>
      ) : rows.length === 0 ? (
        <div className="empty ae-empty">
          <p>{search.trim() ? 'No element matches this search.' : EMPTY_TEXT}</p>
        </div>
      ) : (
        <ul className="owner-picker ae-picker" aria-label="Master elements">
          {rows.map((row) => (
            <CandidateRow
              key={row.engagement_element_id}
              candidate={row}
              chosen={row.engagement_element_id === chosenId}
              onChoose={() => setChosenId(row.engagement_element_id)}
            />
          ))}
        </ul>
      )}

      <div className="ae-country">
        <DialogField
          label="Delivery country"
          htmlFor="add-element-country"
          hint="Needed for billable days on Time & Materials lines"
        >
          <select id="add-element-country" value={countryId} onChange={(event) => setCountryId(event.target.value)}>
            <option value="">No country</option>
            {activeCountries.map((country) => (
              <option key={country.id} value={country.id}>
                {country.name} ({country.code})
              </option>
            ))}
          </select>
        </DialogField>
      </div>
    </Modal>
  );
}
